import React, { useState } from 'react';
import {
  Paper, Stack, TextField, Button, Collapse, FormControlLabel, Checkbox,
  IconButton, Tooltip
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

const INITIAL_DRAFT = {
  memberFullName: '',
  filterAdmitted: false,
  filterDischarged: false,
  filterEmergency: false,
  filterHighCost: false,
  filterHighRisk: false,
};

export default function FiltersBar({ filters, onApplyFilters }) {
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState({
    memberFullName: filters?.memberFullName || '',
    filterAdmitted: !!filters?.filterAdmitted,
    filterDischarged: !!filters?.filterDischarged,
    filterEmergency: !!filters?.filterEmergency,
    filterHighCost: !!filters?.filterHighCost,
    filterHighRisk: !!filters?.filterHighRisk,
  });

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setDraft(prev => ({ ...prev, [field]: value }));
  };

  const handleApply = () => {
    onApplyFilters?.({
      memberFullName: draft.memberFullName.trim() || null,
      filterAdmitted: draft.filterAdmitted || null,
      filterDischarged: draft.filterDischarged || null,
      filterEmergency: draft.filterEmergency || null,
      filterHighCost: draft.filterHighCost || null,
      filterHighRisk: draft.filterHighRisk || null,
    });
  };

  const handleReset = () => {
    setDraft({ ...INITIAL_DRAFT });
    onApplyFilters?.({
      memberFullName: null,
      filterAdmitted: null,
      filterDischarged: null,
      filterEmergency: null,
      filterHighCost: null,
      filterHighRisk: null,
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleApply();
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          size="small"
          placeholder="Search by name..."
          value={draft.memberFullName}
          onChange={handleChange('memberFullName')}
          onKeyDown={handleKeyDown}
          sx={{ flex: 1 }}
        />
        <Tooltip title="Search">
          <IconButton size="small" onClick={handleApply}>
            <SearchIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={expanded ? 'Hide filters' : 'Show filters'}>
          <IconButton size="small" onClick={() => setExpanded(v => !v)}>
            <FilterListIcon color={expanded ? 'primary' : 'inherit'} />
          </IconButton>
        </Tooltip>
      </Stack>

      <Collapse in={expanded}>
        <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: 'wrap' }} alignItems="center">
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={draft.filterAdmitted}
                onChange={handleChange('filterAdmitted')}
              />
            }
            label="Admitted"
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={draft.filterDischarged}
                onChange={handleChange('filterDischarged')}
              />
            }
            label="Discharged"
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={draft.filterEmergency}
                onChange={handleChange('filterEmergency')}
              />
            }
            label="Emergency"
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={draft.filterHighCost}
                onChange={handleChange('filterHighCost')}
              />
            }
            label="High Cost"
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={draft.filterHighRisk}
                onChange={handleChange('filterHighRisk')}
              />
            }
            label="High Risk"
          />
          <Button size="small" variant="outlined" onClick={handleApply}>
            Apply
          </Button>
          <Tooltip title="Reset filters">
            <IconButton size="small" onClick={handleReset}>
              <RestartAltIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Collapse>
    </Paper>
  );
}
